import { useState, useEffect, useRef, useCallback } from 'react';
import { HyperFormula } from 'hyperformula';
import { devLog } from '../utils/devLogger';

interface UseHyperFormulaReturn {
  engine: HyperFormula | null;
  isReady: boolean;
  readOnlyFields: Set<string>;
  sourceToTargets: Map<string, Array<{field: string, weight: number}>>;
  handleFieldChange: (fieldName: string, contextRef: string, value: string) => void;
  getCalculatedValue: (fieldName: string, contextRef: string) => number | null;
  updateFieldValue: (fieldName: string, contextRef: string, value: number | string) => void;
  getFieldValue: (fieldName: string, contextRef?: string) => any;
  refreshCalculations: () => void;
}

/**
 * HyperFormula hook that uses useRef as single source of truth
 * - formDataRef stores all field values: { "fieldName_standardContext": value }
 * - HyperFormula reads from ref and updates ref when calculating
 * - DOM mirrors the ref (UI layer only)
 * - Inputs are uncontrolled (defaultValue from ref, onChange updates ref)
 */
export const useHyperFormula = (
  formStructure: any = null,
  contextRefs: any = null, // Legacy parameter for compatibility
  getFieldValueFromRef?: (fieldName: string, standardContext: string) => string,
  updateRefAndDOM?: (fieldName: string, standardContext: string, value: string) => void
): UseHyperFormulaReturn => {
  const [engine, setEngine] = useState<HyperFormula | null>(null);
  const [isReady, setIsReady] = useState(false);
  const initTimeoutRef = useRef<number | null>(null);
  const formDataLoadedHandlerRef = useRef<(() => void) | null>(null);
  const isMappedRef = useRef(false);
  const pendingFormDataLoadedRef = useRef(false);
  
  // Refs to store the calculation data (exactly like original)
  const sheetRef = useRef<number>(0);
  const cellMapRef = useRef<Record<string, any>>({});
  const readOnlyFieldsRef = useRef<Set<string>>(new Set());
  const sourceToTargetsRef = useRef<Map<string, Array<{field: string, weight: number}>>>(new Map());
  const processedFieldsRef = useRef<Set<string>>(new Set());

  // Process form structure exactly like original script
  const processFormStructure = useCallback((structure: any) => {
    if (!structure || !structure.sections) {
      return;
    }
    
    // Clear previous processing
    readOnlyFieldsRef.current.clear();
    sourceToTargetsRef.current.clear();
    processedFieldsRef.current.clear();
    
    // Process all sections
    for (const section of Object.values(structure.sections)) {
      processSection(section as any);
    }
    
    // Mark calculated fields as readonly (this will be handled by React rendering)
    // readOnlyFieldsRef.current.forEach(field => {
    //   document.querySelectorAll(`input[name="${field}"]`).forEach(input => {
    //     input.setAttribute('readonly', 'readonly');
    //   });
    // });
  }, []);

  const processSection = useCallback((section: any) => {
    if (!section || !section.name) return;
    
    if (section.validation?.calculation?.length > 0) {
      const rules = section.validation.calculation;
      
      const fieldId = `${section.name}_${section.qname}`;
      
      if (!processedFieldsRef.current.has(fieldId)) {
        // First pass: Handle all summation-source rules
        rules.forEach((rule: any) => {
          if (rule.role === 'summation-source') {
            readOnlyFieldsRef.current.add(section.name);
            if (!sourceToTargetsRef.current.has(section.name)) {
              sourceToTargetsRef.current.set(section.name, []);
            }
          }
        });
        
        // Second pass: Handle all summation-item rules
        rules.forEach((rule: any) => {
          if (rule.role === 'summation-item') {
            const sourceField = rule.source;
            if (!sourceToTargetsRef.current.has(sourceField)) {
              sourceToTargetsRef.current.set(sourceField, []);
            }
            sourceToTargetsRef.current.get(sourceField)!.push({
              field: section.name,
              weight: rule.weight || 1
            });
          }
        });
        
        processedFieldsRef.current.add(fieldId);
      }
    }

    if (section.structure?.children) {
      section.structure.children.forEach((child: any) => processSection(child));
    }
  }, []);

  // Map inputs to cells exactly like original script
  // Uses STANDARD contexts (data-year-end) for performance - not real contexts (data-context-ref)
  // This limits HyperFormula to a small set of contexts: current_SeparateMember, comparative_SeparateMember, etc.
  const mapInputsToCells = useCallback((hfEngine: HyperFormula) => {
    let row = 0;
    
    // Get all STANDARD context refs from DOM (data-year-end, not data-context-ref)
    // This is the key performance optimization - only track a few standard contexts
    const contextRefs = new Set<string>();
    document.querySelectorAll('[data-year-end]').forEach(el => {
      const standardContext = el.getAttribute('data-year-end');
      if (standardContext) {
        contextRefs.add(standardContext);
      }
    });
    
    // If no contexts found in DOM yet (React hasn't rendered), use fallback
    // This happens during initial setup before React renders
    if (contextRefs.size === 0) {
      // Fallback: use common standard contexts (will be updated when DOM is ready)
      contextRefs.add('current_SeparateMember');
      contextRefs.add('comparative_SeparateMember');
      contextRefs.add('current_ConsolidatedMember');
      contextRefs.add('comparative_ConsolidatedMember');
    }
    
    // Get all field names from the calculation rules
    const allFields = new Set<string>();
    sourceToTargetsRef.current.forEach((targets, sourceField) => {
      allFields.add(sourceField);
      targets.forEach(target => allFields.add(target.field));
    });
    
    // Map all field-context combinations to cells (using STANDARD contexts)
    allFields.forEach(fieldName => {
      contextRefs.forEach(contextRef => {
        const key = `${fieldName}_${contextRef}`;
        const cellAddress = { sheet: sheetRef.current, col: 0, row: row++ };
        
        // Store in cell map
        cellMapRef.current[key] = cellAddress;
        
        // Read initial value from ref (single source of truth)
        let initialValue = 0;
        if (getFieldValueFromRef) {
          const refValue = getFieldValueFromRef(fieldName, contextRef);
          if (refValue && refValue !== '' && refValue !== '0.00') {
            // Parse and clean the value (remove commas, etc.)
            const numericValue = parseFloat(refValue.replace(/[^\d.-]/g, ''));
            if (!isNaN(numericValue)) {
              initialValue = numericValue;
              // console.log(`HyperFormula: Read initial value from ref for ${fieldName}_${contextRef} = ${initialValue}`);
            }
          }
        } else {
          // Fallback: Read from DOM if ref callback not provided (backward compatibility)
          const inputs = document.querySelectorAll(
            `input[name="${fieldName}"][data-year-end="${contextRef}"]`
          ) as NodeListOf<HTMLInputElement>;
          
          if (inputs.length > 0) {
            const inputValue = inputs[0].value;
            if (inputValue && inputValue !== '' && inputValue !== '0.00') {
              const numericValue = parseFloat(inputValue.replace(/[^\d.-]/g, ''));
              if (!isNaN(numericValue)) {
                initialValue = numericValue;
              }
            }
          }
        }
        
        // Set initial value (from ref if available, otherwise 0)
        hfEngine.setCellContents(cellAddress, initialValue);
      });
    });
  }, [getFieldValueFromRef]);

  // Set up formulas for each context exactly like original
  const setupFormulasForContext = useCallback((hfEngine: HyperFormula, contextRef: string) => {
    // For each source field, create a formula
    sourceToTargetsRef.current.forEach((targets, sourceField) => {
      if (targets.length === 0) return;
      
      const sourceKey = `${sourceField}_${contextRef}`;
      const sourceCell = cellMapRef.current[sourceKey];
      
      if (!sourceCell) return;
      
      // Deduplicate targets to avoid counting fields twice
      const uniqueTargets = new Map<string, number>();
      targets.forEach(item => {
        uniqueTargets.set(item.field, item.weight);
      });
      
      // Build formula: =A1 + B1 + C1...
      let formula = '=';
      const parts: string[] = [];
      
      // Use the uniqueTargets Map to build the formula
      uniqueTargets.forEach((weight, field) => {
        const targetKey = `${field}_${contextRef}`;
        const targetCell = cellMapRef.current[targetKey];
        
        if (targetCell) {
          const cellRef = hfEngine.simpleCellAddressToString(targetCell, sheetRef.current);
          
          if (cellRef) {
            if (weight !== 1) {
              parts.push(`${cellRef}*${weight}`);
            } else {
              parts.push(cellRef);
            }
          }
        }
      });
      
      if (parts.length > 0) {
        formula += parts.join('+');
        
        // Set formula in HyperFormula
        hfEngine.setCellContents(sourceCell, formula);
      }
    });
  }, []);

  // Handle input changes exactly like original script
  const handleInputChange = useCallback((fieldName: string, contextRef: string, value: string) => {
    if (!engine || !isReady) return;
    
    // ONLY process fields that exist in calculation rules (from JSON)
    const hasCalculationRule = readOnlyFieldsRef.current.has(fieldName) || 
                              sourceToTargetsRef.current.has(fieldName) ||
                              Array.from(sourceToTargetsRef.current.values()).some(targets => 
                                targets.some(t => t.field === fieldName)
                              );
    
    if (!hasCalculationRule) {
      return;
    }
    
    const key = `${fieldName}_${contextRef}`;
    const cell = cellMapRef.current[key];
    
    if (!cell) {
      return;
    }
    
    // Parse value exactly like original
    let numericValue = 0;
    if (value && value.trim() !== '') {
      const parsedValue = parseFloat(value.replace(/[^\d.-]/g, '')) || 0;
      numericValue = parsedValue;
    }
    
    // Update cell
    engine.setCellContents(cell, numericValue);
    
    // Update dependent fields
    updateDependentFields(fieldName, contextRef);
  }, [engine, isReady]);

  // Update dependent fields - Update ref first (single source of truth), then DOM
  const updateDependentFields = useCallback((changedField: string, contextRef: string) => {
    if (!engine || !isReady) return;
    
    // Find source fields that depend on this field
    const sourcesToUpdate = new Set<string>();
    
    sourceToTargetsRef.current.forEach((targets, sourceField) => {
      const depends = targets.some(t => t.field === changedField);
      if (depends) {
        sourcesToUpdate.add(sourceField);
      }
    });
    
    // Update each source
    sourcesToUpdate.forEach(sourceField => {
      const sourceKey = `${sourceField}_${contextRef}`;
      const sourceCell = cellMapRef.current[sourceKey];
      
      if (!sourceCell) return;
      
      // Get calculated value
      const value = engine.getCellValue(sourceCell);
      const formattedValue = formatNumber(value);
      
      // Update ref first (single source of truth), then DOM mirrors it
      if (updateRefAndDOM) {
        updateRefAndDOM(sourceField, contextRef, formattedValue);
      } else {
        // Fallback: Update DOM directly if callback not provided (backward compatibility)
        document.querySelectorAll(
          `input[name="${sourceField}"][data-year-end="${contextRef}"]`
        ).forEach((element) => {
          const input = element as HTMLInputElement;
          input.value = formattedValue;
          input.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
            detail: { fieldName: sourceField, contextRef, value: formattedValue }
          }));
        });
      }
      
      // Recursively update any fields that depend on this one
      updateDependentFields(sourceField, contextRef);
    });
  }, [engine, isReady, updateRefAndDOM]);

  // Format number exactly like original script using numeral.js
  const formatNumber = useCallback((value: any): string => {
    // Handle null, undefined, and non-numeric values first
    if (value === null || value === undefined || typeof value !== 'number' || isNaN(value)) {
      value = 0;
    }
    
    // Use numeral.js for formatting (like original script)
    try {
      // Check if numeral is available (like original script does)
      if (typeof window !== 'undefined' && (window as any).numeral) {
        const formatted = (window as any).numeral(value).format('0,0.00');
        if (typeof formatted === 'string' && formatted !== 'NaN') {
          return formatted;
        }
      }
      
      // Basic fallback formatting
      return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    } catch (e) {
      console.warn('Formatting error:', e);
      return "0.00";
    }
  }, []);

  // Get calculated value for a readonly field
  const getCalculatedValue = useCallback((fieldName: string, contextRef: string): number | null => {
    if (!engine || !isReady) return null;
    
    // ONLY return calculated values for fields that exist in calculation rules (from JSON)
    const hasCalculationRule = readOnlyFieldsRef.current.has(fieldName) || 
                              sourceToTargetsRef.current.has(fieldName) ||
                              Array.from(sourceToTargetsRef.current.values()).some(targets => 
                                targets.some(t => t.field === fieldName)
                              );
    
    if (!hasCalculationRule) {
      return null;
    }
    
    const key = `${fieldName}_${contextRef}`;
    const cell = cellMapRef.current[key];
    
    if (!cell) return null;
    
    const value = engine.getCellValue(cell);
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const numValue = parseFloat(value);
      return isNaN(numValue) ? null : numValue;
    }
    return null;
  }, [engine, isReady]);

  // Refresh all calculations (like original)
  const refreshAllCalculations = useCallback(() => {
    if (!engine || !isReady) return;
    
    // Discover all STANDARD context refs from DOM (data-year-end, not data-context-ref)
    // This limits HyperFormula to a small set of contexts for performance
    const contextRefs = new Set<string>();
    document.querySelectorAll('[data-year-end]').forEach(el => {
      const standardContext = el.getAttribute('data-year-end');
      if (standardContext) {
        contextRefs.add(standardContext);
      }
    });
    
    // Set up formulas for each discovered STANDARD context
    Array.from(contextRefs).forEach(contextRef => {
      setupFormulasForContext(engine, contextRef);
    });
  }, [engine, isReady, setupFormulasForContext]);

  // Initialize HyperFormula engine exactly like original
  useEffect(() => {
    // Don't initialize if already initialized OR if formStructure not loaded yet
    if (engine || !formStructure) return;
    
    devLog('🔢 [HYPERFORMULA] Initializing with S3 structure...');
    
    try {
      const hfEngine = HyperFormula.buildEmpty({
        licenseKey: 'gpl-v3'
      });

      // Add sheet
      const sheetName = hfEngine.addSheet('main');
      const sheetId = hfEngine.getSheetId(sheetName);
      sheetRef.current = sheetId || 0;

      // Process form structure (ALWAYS present now due to guard above)
      devLog('🔢 [HYPERFORMULA] Processing form structure...');
      processFormStructure(formStructure);
      
      // Listen for formDataLoaded event to sync values (optimized fast-path)
      // Attach immediately, but if mapping isn't ready yet, defer until mapping completes.
      const handleFormDataLoaded = () => {
        if (!isMappedRef.current) {
          pendingFormDataLoadedRef.current = true;
          return;
        }

        devLog('HyperFormula: formDataLoaded event - fast sync (no remap)...');

        // Discover current STANDARD contexts (small set)
        const updatedContextRefs = new Set<string>();
        document.querySelectorAll('[data-year-end]').forEach(el => {
          const standardContext = el.getAttribute('data-year-end');
          if (standardContext) updatedContextRefs.add(standardContext);
        });

        // Fast path: update ONLY editable cells from ref (do NOT overwrite formula cells)
        Object.entries(cellMapRef.current).forEach(([key, cellAddress]) => {
          const lastUnderscore = key.lastIndexOf('_');
          if (lastUnderscore <= 0) return;

          const fieldName = key.substring(0, lastUnderscore);
          const standardContext = key.substring(lastUnderscore + 1);

          if (!updatedContextRefs.has(standardContext)) return;

          // Skip summation-source cells (they contain formulas)
          if (readOnlyFieldsRef.current.has(fieldName) || sourceToTargetsRef.current.has(fieldName)) {
            return;
          }

          let numericValue = 0;
          if (getFieldValueFromRef) {
            const refValue = getFieldValueFromRef(fieldName, standardContext);
            if (refValue && refValue !== '' && refValue !== '0.00') {
              const parsed = parseFloat(refValue.replace(/[^\d.-]/g, ''));
              if (!isNaN(parsed)) numericValue = parsed;
            }
          } else {
            // Fallback: read from DOM if ref callback not provided
            const input = document.querySelector(
              `input[name="${fieldName}"][data-year-end="${standardContext}"]`
            ) as HTMLInputElement | null;
            if (input && input.value && input.value.trim() !== '') {
              const parsed = parseFloat(input.value.replace(/[^\d.-]/g, ''));
              if (!isNaN(parsed)) numericValue = parsed;
            }
          }

          hfEngine.setCellContents(cellAddress as any, numericValue);
        });

        // Update all summation-source (readonly) fields ONCE per context
        updatedContextRefs.forEach((standardContext) => {
          sourceToTargetsRef.current.forEach((_targets, sourceField) => {
            const sourceKey = `${sourceField}_${standardContext}`;
            const sourceCell = cellMapRef.current[sourceKey];
            if (!sourceCell) return;

            const value = hfEngine.getCellValue(sourceCell);
            const formattedValue = formatNumber(value);

            if (updateRefAndDOM) {
              updateRefAndDOM(sourceField, standardContext, formattedValue);
            } else {
              document.querySelectorAll(
                `input[name="${sourceField}"][data-year-end="${standardContext}"]`
              ).forEach((el) => {
                const input = el as HTMLInputElement;
                input.value = formattedValue;
                input.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
                  detail: { fieldName: sourceField, contextRef: standardContext, value: formattedValue }
                }));
              });
            }
          });
        });

        devLog('HyperFormula: fast sync complete');
        
        // 🔒 FIX: Dispatch event AFTER all DOM updates are done
        // SOFPTable will wait for this before calculating special fields
        document.dispatchEvent(new CustomEvent('hyperformulaCalculationsComplete'));
      };

      formDataLoadedHandlerRef.current = handleFormDataLoaded;
      document.addEventListener('formDataLoaded', handleFormDataLoaded);

      // Wait for DOM to be ready, then map inputs (like 1.0 script)
      // Use setTimeout to allow React to render first
      const initTimeoutId = window.setTimeout(() => {
        mapInputsToCells(hfEngine);
        isMappedRef.current = true;
        
        // Discover all STANDARD context refs from DOM (data-year-end, not data-context-ref)
        // This limits HyperFormula to a small set of contexts for performance
        const contextRefs = new Set<string>();
        document.querySelectorAll('[data-year-end]').forEach(el => {
          const standardContext = el.getAttribute('data-year-end');
          if (standardContext) {
            contextRefs.add(standardContext);
          }
        });
        
        // Set up formulas for each discovered STANDARD context
        Array.from(contextRefs).forEach(contextRef => {
          setupFormulasForContext(hfEngine, contextRef);
        });
        
        // If formDataLoaded fired early (before mapping), run it once now.
        if (pendingFormDataLoadedRef.current && formDataLoadedHandlerRef.current) {
          pendingFormDataLoadedRef.current = false;
          formDataLoadedHandlerRef.current();
        }
      }, 100);
      initTimeoutRef.current = initTimeoutId;

      setEngine(hfEngine);
      setIsReady(true);
      devLog('✅ [HYPERFORMULA] Initialization complete');
    } catch (error) {
      console.error('❌ [HYPERFORMULA] Initialization failed:', error);
      setIsReady(false);
    }
    return () => {
      // Cleanup: remove listener and clear init timeout to avoid duplicate handlers
      if (initTimeoutRef.current !== null) {
        window.clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      if (formDataLoadedHandlerRef.current) {
        document.removeEventListener('formDataLoaded', formDataLoadedHandlerRef.current);
        formDataLoadedHandlerRef.current = null;
      }
      isMappedRef.current = false;
      pendingFormDataLoadedRef.current = false;
    };
  }, [formStructure, processFormStructure, mapInputsToCells, setupFormulasForContext, formatNumber, updateRefAndDOM, getFieldValueFromRef]); // Run when formStructure first loads

  // Legacy compatibility methods for FinancialForm
  const updateFieldValue = useCallback((fieldName: string, contextRef: string, value: number | string) => {
    handleInputChange(fieldName, contextRef, value.toString());
  }, []);

  const getFieldValue = useCallback((fieldName: string, contextRef?: string) => {
    if (!contextRef) return null;
    return getCalculatedValue(fieldName, contextRef);
  }, [getCalculatedValue]);

  return {
    engine,
    isReady,
    readOnlyFields: readOnlyFieldsRef.current,
    sourceToTargets: sourceToTargetsRef.current,
    handleFieldChange: handleInputChange,
    getCalculatedValue,
    updateFieldValue,
    getFieldValue,
    refreshCalculations: refreshAllCalculations,
  };
};